import { colors, flowBuilderConstants } from '../../../../constants/FlowBuilder';
import CanvasText from '../../canvas-text/CanvasText';
import { drawCanvasImage, drawRoundedRect } from '../../helpers';
import { CanvasElementBtn, CanvasElementConfig, Position, PositionWithSize } from '../../TypeDefs';

/**
 * @method drawConditionCard
 * @description Draw condition card
 * @param ctx canvas 2d context
 * @param conditionText Condition text
 */
export const drawConditionCard = ({
  conditionText = '',
  ctx,
  conditionBoxPosition,
  hasConvergence,
  config,
  incomingHY,
  incomingHX
}: {
  ctx: CanvasRenderingContext2D,
  conditionText?: string,
  conditionBoxPosition: Position,
  hasConvergence: boolean,
  config: CanvasElementConfig,
  incomingHY: number,
  incomingHX: number,
}) => {
  if (!hasConvergence || !conditionText) {
    return;
  }
  const conditionCenterY = incomingHY;
  const innerCircleRadius = flowBuilderConstants.conditionTextBoxSize.h / 2;

  ctx.fillStyle = config.conditionTextBgColor;
  drawRoundedRect(
    ctx,
    conditionBoxPosition.left, // x
    conditionBoxPosition.top, // y
    flowBuilderConstants.conditionTextBoxSize.w, // width
    flowBuilderConstants.conditionTextBoxSize.h, // height
    flowBuilderConstants.conditionTextBoxSize.h / 2
  );
  ctx.fill();
  ctx.strokeStyle = colors.conditionText;
  ctx.stroke();

  // draw the circle
  ctx.shadowBlur = 4;
  ctx.shadowColor = 'rgba(0,0,0,0.5)';
  ctx.fillStyle = config.conditionTextFgColor;
  ctx.beginPath();
  if (conditionText === 'OR') {
    ctx.arc(
      conditionBoxPosition.right - innerCircleRadius,
      conditionCenterY,
      innerCircleRadius - 7,
      0,
      2 * Math.PI
    );
  } else {
    ctx.arc(
      conditionBoxPosition.left + innerCircleRadius,
      conditionCenterY,
      innerCircleRadius - 7,
      0,
      2 * Math.PI
    );
  }
  ctx.fill();
  ctx.shadowBlur = 0;

  let textOffset = innerCircleRadius / 2;
  if (conditionText !== 'OR') {
    textOffset = -textOffset;
  }
  ctx.fillStyle = config.conditionTextFgColor;
  ctx.font = `400 ${config.conditionTextFontSize}px '${config.fontFamily}'`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(
    conditionText,
    incomingHX - flowBuilderConstants.conditionTextBoxOffset.x + flowBuilderConstants.conditionTextBoxSize.w / 2 - textOffset,
    conditionCenterY + 2
  );

  // draw connector from and/or to card
  ctx.beginPath();
  ctx.moveTo(incomingHX - flowBuilderConstants.conditionTextBoxOffset.x + flowBuilderConstants.conditionTextBoxSize.w, incomingHY);
  ctx.lineTo(incomingHX, incomingHY);
  ctx.strokeStyle = config.conditionTextConnectorStrokeColor;
  ctx.lineWidth = config.conditionTextConnectorStrokeWidth;
  ctx.stroke();

  // draw arrow head
  ctx.fillStyle = config.conditionTextConnectorStrokeColor;
  ctx.beginPath();
  ctx.moveTo(incomingHX, incomingHY);
  ctx.lineTo(incomingHX - config.outConnectorRadius * 2, incomingHY - config.outConnectorRadius * 1.2);
  ctx.lineTo(incomingHX - config.outConnectorRadius * 2, incomingHY + config.outConnectorRadius * 1.2);
  ctx.lineTo(incomingHX, incomingHY);
  ctx.lineTo(incomingHX - config.outConnectorRadius * 2, incomingHY - config.outConnectorRadius * 1.2);

  ctx.fill();
};

/**
   * @method drawButtons
   * @description Draw buttons on canvas
   * @param ctx canvas 2d context
   */
export const drawButtons = ({
  btnGroupPosition,
  ctx,
  config,
  buttons
}: {
  btnGroupPosition: PositionWithSize,
  ctx: CanvasRenderingContext2D,
  config: CanvasElementConfig,
  buttons: CanvasElementBtn[]
}) => {
  ctx.fillStyle = config.btnGroupBackgroundColor;
  drawRoundedRect(
    ctx,
    btnGroupPosition.left,
    btnGroupPosition.top,
    btnGroupPosition.width,
    btnGroupPosition.height * 2,
    config.btnGroupBorderRadius
  );
  ctx.fill();

  const btnIconTop = btnGroupPosition.top + 8;
  let btnIconLeft = btnGroupPosition.left + 12;
  buttons.forEach((button) => {
    drawCanvasImage(ctx, button.image, btnIconLeft, btnIconTop, button.width, button.height);
    button.top = btnIconTop;
    button.left = btnIconLeft;
    button.bottom = button.top + button.height;
    button.right = button.left + button.width;
    btnIconLeft += button.width + 12;
  });
};

/**
   * @method drawDescriptionText
   * @description Draw description text
   * @param ctx canvas 2d context
   * @param descriptionFont description
   * @param nameTextX nameTextX
   * @param centerY centerY
   * @param descriptionLineHeight description line height
   */
export const drawDescriptionText = (
  {
    mDescription,
    ctx,
    centerY,
    descriptionFont,
    descriptionLineHeight,
    nameTextX,
    isSelected,
    payloadTransformWarn: payloadTransform,
    config
  }: {
    mDescription: CanvasText,
    ctx: CanvasRenderingContext2D,
    descriptionFont: string,
    nameTextX: number,
    centerY: number,
    descriptionLineHeight: number,
    isSelected: boolean,
    payloadTransformWarn?: boolean,
    config: CanvasElementConfig
  }
) => {
  // draw the description
  if (isSelected && mDescription.text.length > 0) {
    ctx.font = descriptionFont;
    ctx.fillStyle = isSelected
      ? config.selectedDescriptionColor
      : config.descriptionColor;
    mDescription.brokenLines.forEach((line, idx) => {
      ctx.fillText(
        line,
        nameTextX,
        payloadTransform ? centerY + flowBuilderConstants.elementDescriptionMargin + ((idx + 1) * descriptionLineHeight) + 30 : centerY + flowBuilderConstants.elementDescriptionMargin + ((idx + 1) * descriptionLineHeight)
      );
    });
  }
};

export const drawPayloadTransformationWarning = (
  {
    ctx,
    centerY,
    payloadFont,
    payloadLineHeight,
    nameTextX,
    payloadTransformWarn: payloadTransform,
    config,
    addPayloadText,
    nameLineHeight
  }: {
    ctx: CanvasRenderingContext2D,
    payloadFont: string,
    nameTextX: number,
    centerY: number,
    payloadLineHeight: number,
    payloadTransformWarn?: boolean,
    config: CanvasElementConfig,
    addPayloadText: CanvasText,
    nameLineHeight: number
  }
) => {
  // draw the payload
  if (payloadTransform && addPayloadText.text.length > 0) {
    ctx.font = payloadFont;
    ctx.fillStyle = config.errorColor;
    addPayloadText.brokenLines.forEach((line, idx) => {
      ctx.fillText(
        line,
        nameTextX + nameLineHeight,
        centerY - nameLineHeight + ((idx + 1) * payloadLineHeight) - 6,
      );
    });
  }
};

/**
 * @method drawNameText
 * @description Draw Name text
 * @param ctx canvas 2d context
 * @param nameFont name
 * @param nameTextX nameTextX
 * @param centerY centerY
 */
export const drawNameText = ({
  ctx,
  centerY,
  nameFont,
  nameTextX,
  mName,
  isSelected,
  config,
  left,
  nameLineHeight = 0,
  payloadTransformWarn
}: {
  ctx: CanvasRenderingContext2D,
  nameFont: string,
  nameTextX: number,
  centerY: number,
  mName: CanvasText,
  isSelected: boolean,
  config: CanvasElementConfig,
  left: number,
  nameLineHeight?: number,
  payloadTransformWarn?: boolean
}) => {
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  // draw the name
  if (mName.text.length > 0) {
    ctx.font = nameFont;
    ctx.fillStyle = isSelected
      ? config.selectedNameColor
      : config.nameColor;
    mName.ellipsisText(
      ctx,
      config.width - (nameTextX - left) - config.padding * 2,
    );
    ctx.fillText(
      mName.brokenLines[0],
      nameTextX,
      payloadTransformWarn ? centerY + nameLineHeight - 4 : centerY - (nameLineHeight / 2) + 4,
    );
  }
};

/**
 * @method drawVersionText
 * @description Draw Name text
 * @param ctx canvas 2d context
 * @param nameFont name
 * @param nameTextX nameTextX
 * @param centerY centerY
 */
export const drawVersionText = ({
  ctx,
  centerY,
  versionFont,
  versionTextX,
  versionLineHeight,
  mVersion,
  isSelected,
  config,
  left,
  payloadTransformWarn: payloadTransform
}: {
  ctx: CanvasRenderingContext2D,
  versionFont: string,
  versionTextX: number,
  versionLineHeight: number,
  centerY: number,
  mVersion: CanvasText,
  isSelected: boolean,
  config: CanvasElementConfig,
  left: number,
  payloadTransformWarn?: boolean
}) => {
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  // draw the name
  if (mVersion.text.length > 0) {
    ctx.font = versionFont;
    ctx.fillStyle = isSelected
      ? config.selectedNameColor
      : config.nameColor;
    mVersion.ellipsisText(
      ctx,
      config.width - (versionTextX - left) - config.padding * 2,
    );
    ctx.fillText(
      mVersion.brokenLines[0],
      versionTextX,
      payloadTransform ? centerY + flowBuilderConstants.elementVersionMargin + (versionLineHeight * 2) + 1 : centerY + flowBuilderConstants.elementVersionMargin + (versionLineHeight / 2) + 1,
    );
  }
};

/**
 * @method prepareHeightForDraw
 * @description Calculates the height of the canvas element for drawing based on various conditions.
 *
 * @param ctx - The canvas rendering context.
 * @param descriptionLineHeight - The height of each line in the description.
 * @param isSelected - Indicates whether the canvas element is selected.
 * @param config - The configuration object for the canvas element.
 * @param mDescription - The canvas text object representing the description.
 * @param conditionText - The condition text.
 * @param payloadTransform - Indicates whether the payload is transformed.
 * @returns The calculated height of the canvas element.
 */
const prepareHeightForDraw = ({
  ctx,
  descriptionLineHeight,
  isSelected,
  config,
  mDescription,
  conditionText,
  payloadTransformWarn
}: {
  ctx: CanvasRenderingContext2D,
  descriptionLineHeight: number,
  isSelected: boolean,
  config: CanvasElementConfig,
  mDescription: CanvasText,
  conditionText?: string,
  payloadTransformWarn?: boolean
}) => {
  let mHeight = config.height;
  const isPayloadWarning = ((conditionText === 'AND' || conditionText === 'T-AND') && payloadTransformWarn);
  if (isSelected && isPayloadWarning) {
    // calculate the height when payload transformation warning is present
    mHeight = (config.height + (mDescription.brokenLines.length - 0.5) * descriptionLineHeight + flowBuilderConstants.elementDescriptionMargin + 25);
    ctx.strokeStyle = config.errorColor;
  } else if (isSelected && !payloadTransformWarn) {
    mHeight = (config.height + (mDescription.brokenLines.length - 0.5) * descriptionLineHeight + flowBuilderConstants.elementDescriptionMargin);
  } else if ((!isSelected && payloadTransformWarn)) {
    mHeight = (config.height + flowBuilderConstants.elementDescriptionMargin + 10);
    ctx.strokeStyle = config.errorColor;
  }

  return mHeight;
};

/**
 * @method drawBody
 * @description Draw Body
 * @param ctx canvas 2d context
 * @param descriptionLineHeight description line height
 */
export const drawBody = ({
  ctx,
  cardRadius,
  descriptionLineHeight,
  drawingStep,
  isSelected,
  config,
  isError,
  mDescription,
  top,
  isStackStyled,
  left,
  conditionText,
  payloadTransformWarn
}: {
  ctx: CanvasRenderingContext2D,
  descriptionLineHeight: number,
  cardRadius: number,
  drawingStep: boolean,
  isSelected: boolean,
  config: CanvasElementConfig,
  isError: boolean,
  mDescription: CanvasText,
  top: number,
  isStackStyled: boolean,
  left: number,
  conditionText?: string,
  payloadTransformWarn?: boolean
}) => {
  ctx.setLineDash([]);

  ctx.beginPath();
  ctx.fillStyle = isSelected
    ? config.selectedFillColor
    : config.fillColor;
  ctx.strokeStyle = config.strokeColor;
  if (isSelected) {
    ctx.strokeStyle = config.selectedStrokeColor;
  } else if (isError) {
    ctx.strokeStyle = config.errorColor;
  }

  ctx.lineWidth = config.strokeWidth;
  const blurCondition = cardRadius ? 0 : 15;
  ctx.shadowBlur = isSelected ? 20 : blurCondition;

  ctx.shadowOffsetY = cardRadius ? 0 : 2;
  ctx.shadowColor = 'rgba(0,0,0,0.6)';
  if (isSelected) {
    ctx.shadowOffsetX = 10;
    ctx.shadowOffsetY = 10;
    ctx.shadowColor = 'rgb(121, 87, 231)'; // 'rgba(0,0,0,0.6)';
  }

  const mHeight = prepareHeightForDraw({
    config,
    ctx,
    descriptionLineHeight,
    isSelected,
    mDescription,
    conditionText,
    payloadTransformWarn
  });

  if (isStackStyled) {
    drawRoundedRect(
      ctx,
      left + flowBuilderConstants.compensatoryDummyCardOffset,
      top + flowBuilderConstants.compensatoryDummyCardOffset,
      config.width,
      mHeight,
      cardRadius,
    );
    ctx.fill();

    if (cardRadius) {
      ctx.globalAlpha = 1;
      ctx.strokeStyle = config.nameColor;
      ctx.setLineDash([4, 2]);
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }

  drawRoundedRect(
    ctx,
    left,
    top,
    config.width,
    mHeight,
    cardRadius,
  );

  ctx.fill();
  // restore shadow
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  if (config.strokeWidth > 0 && ctx.fillStyle !== ctx.strokeStyle) {
    ctx.stroke();
  }

  if (drawingStep) {
    ctx.globalAlpha = 1;
    ctx.strokeStyle = config.nameColor;
    ctx.setLineDash([4, 2]);
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  return {
    height: mHeight,
  };
};
